/**
 * Support-report capture — Phase 1.
 *
 * Data: unbound. The report SUBMIT (`createSupportReport` →
 * POST /v1/support/reports) is declared only; no such entity or operation
 * exists in OPERATIONS_REGISTRY, so the call is issued for real and fails for
 * real until the backend lands (operator ruling, 2026-09-21, Nick: "let it
 * fail"). Nothing here fakes a success. REQ-SR-01.
 *
 * WHAT THIS FILE IS. A small buffer beside the client, not a per-screen
 * integration. Three sources, all of them already real:
 *   1. `getRecentApiCalls()` from @v0y4ge/api-client telemetry — every typed
 *      call the app makes, recorded INSIDE the client at its four fetch choke
 *      points, with id-shaped path segments already collapsed to `:id`. That
 *      collapse is not our doing and not defeatable from a screen, which is
 *      what makes "no resolved URL, no record id" structural here.
 *   2. `noteApiError()` — the error CODE and request id, which telemetry does
 *      not carry (it records status only). A screen that already catches an
 *      ApiError can report it here. KNOWN GAP, filed rather than papered over:
 *      screens that do not call it leave the report with a status and no code.
 *   3. `noteUiEvent()` — a short ring of typed UI events (route changes and
 *      the like). Ids and kinds only. Never a value, never element content.
 *
 * WHAT MAY NEVER GO IN. No field values, no record ids, no rendered strings, no
 * row indices, no request/response bodies, no URLs with ids resolved into them.
 * The allow-list is enforced server-side too when the endpoint exists; this
 * file is the client half of the same rule, and every field below is either an
 * identifier we minted, a count, or an enum value we wrote ourselves.
 */
import { getRecentApiCalls, apiRaw } from "@v0y4ge/api-client";

const STORE_KEY = "studio.support.capture.v1";
const MAX_EVENTS = 20;
const MAX_ERRORS = 5;

export interface UiEventRecord {
  at: string;
  /** Typed kind: "route" | "dialog" | "picker". Never free text. */
  kind: string;
  /** An id we minted (a route id, a dialog name). Never a value or a label. */
  detail: string;
}

export interface NotedApiError {
  at: string;
  operationId: string;
  /** Wire error code, e.g. CUSTOMER_TYPE_CONFLICT. Our own vocabulary. */
  code: string;
  /** Closed-vocabulary recovery hint from ApiError.problem. */
  problem: string | null;
  requestId: string | null;
  status: number;
}

export interface CapturedFailure {
  operationId: string;
  method: string;
  /** Already `:id`-collapsed by the client's own telemetry. */
  pathTemplate: string;
  status: number;
  durationMs: number;
  occurredAt: string;
  code: string | null;
  problem: string | null;
  requestId: string | null;
}

export interface PickedControl {
  /** From `data-jrni-component`, or the element's tag when absent. */
  component: string;
  /** Component ancestry, design-system names where they exist:
   *  `modal > modal-body > button`. Schema, and often enough on its own. */
  componentPath: string;
  /** From `data-testid` / `data-jrni-*`. Null when the control carries none. */
  testid: string | null;
  /** From `data-field`, else derived from the testid. Never a value. */
  field: string | null;
  role: string;
  disabled: boolean;
  /** Position among the focusable fields of the same region — NOT a row index.
   *  Row identity is excluded by construction: "row 3 of the filtered list"
   *  plus the active filters can single out a person. */
  fieldPosition: { index: number; total: number } | null;
  /** How the control was identified, strongest first. Every control resolves
   *  to one of these — there is no "could not identify" outcome. */
  identity: "testid" | "component" | "structure";
  /** Structural DOM path, indices omitted anywhere inside a list or table row
   *  (see the picker's note on row identity). Tags and component names only —
   *  no classes, no ids, no text. */
  structurePath: string;
  /** Short stable hash of `structurePath`. Two reports about the same control
   *  carry the same signature, so they group even with no test id. */
  signature: string;
  /** NAMES of structural attributes present, plus values for the handful that
   *  are author-written schema or state (`type`, `name`, `aria-expanded`).
   *  Never `value`, `aria-label`, `title`, `placeholder` or `alt` — all four
   *  routinely interpolate a customer's name. */
  attrs: string[];
  /** False when the control had no test id. Kept as its own field because it
   *  is a COVERAGE finding for us, not a degradation for the reporter. */
  labelled: boolean;
}

interface CaptureState {
  events: UiEventRecord[];
  errors: NotedApiError[];
}

let state: CaptureState = load();

function load(): CaptureState {
  /* sessionStorage, not memory (operator ruling, 2026-09-21): reloading the
     page is the first thing people do when something breaks, and losing the
     failure they are about to report to that reflex makes the tool useless
     exactly when it is needed. Session-scoped, so it dies with the tab — and
     it holds no values, so a shared branch terminal leaks nothing. */
  if (typeof window === "undefined") return { events: [], errors: [] };
  try {
    const raw = window.sessionStorage.getItem(STORE_KEY);
    if (!raw) return { events: [], errors: [] };
    const parsed = JSON.parse(raw) as Partial<CaptureState>;
    return {
      events: Array.isArray(parsed.events) ? parsed.events.slice(-MAX_EVENTS) : [],
      errors: Array.isArray(parsed.errors) ? parsed.errors.slice(-MAX_ERRORS) : [],
    };
  } catch {
    return { events: [], errors: [] };
  }
}

function persist(): void {
  if (typeof window === "undefined") return;
  try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch { /* quota / private mode */ }
}

export function noteUiEvent(kind: string, detail: string): void {
  state.events = [...state.events, { at: new Date().toISOString(), kind, detail }].slice(-MAX_EVENTS);
  persist();
}

/**
 * Drop everything captured under the previous tenant.
 *
 * The buffer is not tenant-partitioned and must not become so by accident: a
 * user who switches workspace and then reports a problem would otherwise send
 * route ids and error codes gathered inside a DIFFERENT tenant. Route ids are
 * not customer data, but they are that tenant's operational detail, and Part 7
 * of the data model is explicit that nothing crosses a boundary — not even
 * existence. Cheaper to forget than to partition.
 */
export function resetCaptureForTenant(): void {
  state = { events: [], errors: [] };
  persist();
}

/** Shape-only read of an unknown throwable. Deliberately duck-typed rather than
 *  `instanceof ApiError` — the client's own doc comment says instanceof lies
 *  across realms, and the brand field is the supported identity check. */
export function noteApiError(err: unknown, operationId?: string): void {
  if (!err || typeof err !== "object") return;
  if (operationId && SELF_OPS.has(operationId)) return;
  const e = err as { apiErrorBrand?: string; code?: unknown; problem?: unknown; requestId?: unknown; status?: unknown };
  if (typeof e.code !== "string") return;
  state.errors = [...state.errors, {
    at: new Date().toISOString(),
    operationId: operationId || "unknown",
    code: e.code,
    problem: typeof e.problem === "string" ? e.problem : null,
    requestId: typeof e.requestId === "string" ? e.requestId : null,
    status: typeof e.status === "number" ? e.status : 0,
  }].slice(-MAX_ERRORS);
  persist();
}

export function clearCapture(): void {
  state = { events: [], errors: [] };
  if (typeof window === "undefined") return;
  try { window.sessionStorage.removeItem(STORE_KEY); } catch { /* noop */ }
}

export function recentUiEvents(): UiEventRecord[] { return state.events; }
export function recentApiErrors(): NotedApiError[] { return state.errors; }

/** Path only — scheme, host and query removed.
 *
 *  The client's telemetry stores whatever it was handed as the path, which in
 *  the preview harness (and anywhere a call is made against an absolute base)
 *  is a full URL including the host. A host is not diagnostic — every report
 *  from a tenant has the same one — and in the preview it is the design-tool
 *  origin, which has no business in a support ticket. Stripped here rather
 *  than at render, so nothing downstream can reintroduce it. */
function pathOnly(raw: string): string {
  let s = raw.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, "");
  s = s.split("?")[0].split("#")[0];
  return s || "/";
}

/** Ops that must never be reported AS the failure. `createSupportReport` is the
 *  reporting tool's own write: once a send fails it becomes the most recent
 *  failed call, so the NEXT report would describe the reporter failing rather
 *  than the problem the user is reporting. Self-exclusion, not cosmetics. */
const SELF_OPS = new Set(["createSupportReport"]);

/** Background infrastructure the user did not initiate. A 404 from the locale
 *  catalog or a capability probe is not "the thing that went wrong" from the
 *  reporter's point of view, but it lands in the same ring buffer and, being
 *  more recent, used to win. Blaming the translation catalog for a failed save
 *  is worse than reporting no failure at all — it sends support down a false
 *  trail with our own authority behind it. */
const INFRA_OP_RE = /translation|catalog|i18n|locale|capabilit|whoami|telemetry|navpins|nav_pins|heartbeat|health/i;
const INFRA_PATH_RE = /\/(i18n|translations?|catalog|capabilities|health)(\/|$)/i;
const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** When the current route was entered, from the event ring. Failures older than
 *  this belong to a screen the reporter has already left. */
function currentRouteEnteredAt(): string | null {
  for (let i = state.events.length - 1; i >= 0; i--) {
    if (state.events[i].kind === "route") return state.events[i].at;
  }
  return null;
}

/**
 * The failure this report is ABOUT, or null.
 *
 * `null` is the COMMON case and the dialog must render it as such: most reports
 * are "it saved the wrong thing" or "the list is missing someone", where nothing
 * threw at all (design doc §6). A report that implies a request was captured
 * when none was is worse than one that says so.
 */
export function lastFailure(): CapturedFailure | null {
  let calls: ReturnType<typeof getRecentApiCalls> = [];
  try { calls = getRecentApiCalls(50); } catch { return null; }
  const since = currentRouteEnteredAt();
  const candidates = calls.filter((c) => {
    if (c.result === "ok") return false;
    if (SELF_OPS.has(c.operationId)) return false;
    const path = pathOnly(c.pathTemplate);
    if (path.startsWith("/v1/support/")) return false;
    if (INFRA_OP_RE.test(c.operationId) || INFRA_PATH_RE.test(path)) return false;
    /* Scoped to the screen being reported. Without this, a failure from three
       screens ago outranks the one in front of the reporter. */
    if (since && c.occurredAt < since) return false;
    return true;
  });
  if (!candidates.length) return null;
  /* A failed write is almost always the complaint; a failed read is usually a
     symptom of it. Most recent mutation wins, then most recent anything. */
  const byRecency = candidates.slice().sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1));
  const failed = byRecency.find((c) => MUTATION_METHODS.has((c.method || "").toUpperCase())) || byRecency[0];
  if (!failed) return null;
  const noted = state.errors.slice().reverse().find((e) => e.operationId === failed.operationId) || null;
  return {
    operationId: failed.operationId,
    method: failed.method,
    pathTemplate: pathOnly(failed.pathTemplate),
    status: failed.status,
    durationMs: failed.durationMs,
    occurredAt: failed.occurredAt,
    code: noted ? noted.code : null,
    problem: noted ? noted.problem : null,
    requestId: noted ? noted.requestId : null,
  };
}

/** Error codes seen this session, newest first — the real source behind
 *  "Was it one of these messages?". The doc's i18n-key version needs error copy
 *  moved into the catalog first (the `errors.*` namespace is effectively one
 *  key today), so codes carry it now and catalog keys fold in as they land. */
export function sessionErrorCodes(): NotedApiError[] {
  return state.errors.slice().reverse();
}

export interface SupportReportPayload {
  /** Which entry point opened the dialog. An ambient report (profile menu)
   *  and a contextual one (error toast) read very differently in a ticket. */
  source: string;
  /** Fixed enums and ids only — see the file header. */
  intent: string | null;
  area: string | null;
  outcome: string | null;
  frequency: string | null;
  lastWorked: string | null;
  /** The English phrases behind those ids. The ids group reports mechanically;
   *  the phrases are what a support engineer actually reads, and Figure 5 of
   *  the design doc prints the phrase, not the id. English deliberately — the
   *  ticket is read by us, not by the reporter, so this must not follow the
   *  reporter's locale. */
  labels: {
    intent: string | null;
    area: string | null;
    outcome: string | null;
    frequency: string | null;
    lastWorked: string | null;
  };
  vocabularyFamily: string;
  messageCode: string | null;
  pointedAt: PickedControl | null;
  failure: CapturedFailure | null;
  screen: { route: string; navPath: string[]; secondsOnScreen: number };
  reporter: { role: string; userId: string | null; locale: string; impersonated: boolean };
  tenant: { slug: string; plan: string };
  client: { browser: string; viewport: string };
  recentEvents: UiEventRecord[];
  /** Report came from preview/seed-served data rather than a live tenant.
   *  Without this, support investigates bugs that only exist in a demo. */
  previewData: boolean;
  /** Reports filed from this tab, including this one. */
  occurrenceThisSession: number;
}

/** Browser family + version only. Never the full user-agent string, which is a
 *  fingerprint. Best-effort parse; "unknown" is an acceptable answer. */
export function browserLabel(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const m = /(Firefox|Edg|Chrome|Safari)\/([0-9]+)/.exec(ua.replace("Version/", "Safari/"));
  if (!m) return "unknown";
  const name = m[1] === "Edg" ? "Edge" : m[1];
  return `${name} ${m[2]}`;
}

/** Reports filed from this tab. "1 this session" is free in the client;
 *  "3 this tenant in 24 h" needs a counting query over a report store that
 *  does not exist, so the tenant half says so rather than inventing a number. */
let _sessionReports = 0;
export function countReportFiled(): number { _sessionReports += 1; return _sessionReports; }

/** Figure 5's email body, verbatim — the same text that goes into the ticket,
 *  and the same text "Copy report as text" puts on the clipboard when the send
 *  fails. There is no second payload anywhere.
 *
 *  Fields the design specifies but Phase 1 cannot source are PRINTED AS
 *  UNAVAILABLE rather than dropped. A support engineer reading this needs to
 *  know the difference between "no unsaved fields" and "nobody counted", and
 *  the absent lines are the to-do list for the backend half (REQ-SR-01). */
export function renderReportText(p: SupportReportPayload, reference: string | null): string {
  const L: string[] = [];
  const row = (k: string, val: string) => L.push(`${k.padEnd(16)}${val}`);
  const gap = () => L.push("");
  const f = p.failure;
  const subjectCode = f?.code || (f ? `HTTP_${f.status}` : "NO_ERROR_CAPTURED");

  L.push(`SUBJECT  [JAI] ${subjectCode} · ${p.screen.route} · ${p.tenant.slug} · ${reference || "unsent"}`);
  gap();
  L.push("A problem was reported from the Studio app. This message contains no");
  L.push("customer personal data — every field below is a fixed allow-list value.");
  gap();

  if (reference) row("Reference", reference);
  else row("Reference", "none — the report was not accepted by the server");
  row("Filed", `${new Date().toISOString()} · ${new Date().toLocaleTimeString()} local`);
  row("Tenant", `${p.tenant.slug} · plan ${p.tenant.plan} · region not captured (deployment fact, stamped server-side)`);
  row("Reporter", `role ${p.reporter.role} · ${p.reporter.userId || "id not captured"} · ${p.reporter.locale}`);
  if (p.reporter.impersonated) row("", "FILED WHILE IMPERSONATING — the acting user is not the account holder");
  row("Started from", p.source);
  if (p.previewData) row("Data source", "PREVIEW / seed-served — NOT a live tenant");
  const withId = (label: string | null, id: string | null) => (label ? `${label}  (${id})` : id || "—");
  row("Intent", p.labels.intent ? `${p.labels.intent}  (picked from a list)` : "—");
  row("Area", withId(p.labels.area, p.area));
  row("Outcome", withId(p.labels.outcome, p.outcome));
  row("Message", p.messageCode ? `${p.messageCode}  (error code — not the rendered string)` : "—");
  row("Frequency", `${p.labels.frequency || "—"} · last worked ${p.labels.lastWorked || "—"}`);
  if (p.pointedAt) {
    row("Pointed at", `${p.pointedAt.component} · ${p.pointedAt.testid || "no test id"} · field ${p.pointedAt.field || "—"}`);
    row("", `${p.pointedAt.role}${p.pointedAt.disabled ? " · disabled" : ""}${p.pointedAt.fieldPosition ? ` · ${p.pointedAt.fieldPosition.index} of ${p.pointedAt.fieldPosition.total} fields` : ""}  (no value, no row index)`);
    row("", `${p.pointedAt.componentPath}`);
    row("", `${p.pointedAt.structurePath}`);
    row("Control id", `${p.pointedAt.signature} · identified by ${p.pointedAt.identity}`);
    if (p.pointedAt.attrs.length) row("Attributes", p.pointedAt.attrs.join(" · "));
    if (!p.pointedAt.labelled) row("", "NO TEST ID on this control — test-id coverage gap, worth closing");
  } else {
    row("Pointed at", "—");
  }
  gap();

  if (f) {
    L.push(`${f.method} ${f.pathTemplate} → ${f.status}`);
    row("Operation", f.operationId);
    row("Error code", f.code ? `${f.code}${f.problem ? ` · ${f.problem}` : ""}` : "not captured — the screen did not report one (REQ-SR-01c)");
    row("Request id", `${f.requestId || "not captured"} · ${f.durationMs} ms`);
    row("Change", "not captured — no screen reports the attempted change yet");
    row("Subject", "not captured — the per-tenant digest scheme is undesigned");
  } else {
    L.push("No failed request was captured.");
    L.push("Nothing threw — the outcome, area and frequency answers above are the");
    L.push("whole diagnostic payload. This is the common case, not a capture bug.");
  }
  gap();

  row("Screen", p.screen.route);
  row("Nav path", p.screen.navPath.join(" → ") || p.screen.route);
  row("On screen", `${p.screen.secondsOnScreen}s before reporting`);
  row("Build", "not captured — no version constant reaches the client (REQ-SR-01d)");
  row("Client", `${p.client.browser} · viewport ${p.client.viewport}`);
  row("Role caps", "not captured");
  gap();

  row("Record shape", "not captured — counted at capture only where a screen reports it");
  row("Unsaved on form", "not captured");
  row("Occurrence", `${p.occurrenceThisSession} this session · tenant count needs the report store`);
  if (p.recentEvents.length) {
    gap();
    L.push("Recent actions (ids only, no values)");
    p.recentEvents.slice(-8).forEach((e) => L.push(`  ${e.at}  ${e.kind}  ${e.detail}`));
  }
  gap();
  L.push("Need the record itself to diagnose this? Reply in the ticket and ask the");
  L.push("tenant. There is no stored copy and no link — by design.");
  return L.join("\n");
}

export interface SubmitResult { reference: string }

/**
 * The one call site for the declared support-report write.
 *
 * DECLARED ONLY — `createSupportReport` is in no contract and this endpoint
 * does not exist, so this rejects every time today. That is deliberate and
 * operator-chosen: the alternative was a success toast with nothing behind it,
 * which the no-fake-interactivity mandate forbids outright. The dialog awaits
 * this and renders the real failure, with Retry and Copy-as-text as the escape.
 *
 * ## Consumed ops
 * - `createSupportReport(payload)` → POST /v1/support/reports — support/capture.ts (DECLARED, not implemented — REQ-SR-01)
 */
export async function submitSupportReport(payload: SupportReportPayload): Promise<SubmitResult> {
  const res = await apiRaw<{ reference?: string }>(
    "/v1/support/reports",
    { method: "POST", body: JSON.stringify(payload) },
    "createSupportReport",
  );
  return { reference: typeof res?.reference === "string" ? res.reference : "—" };
}
